/* Visualizador de Ambientes — La Casa de la Cerámica
 * Detecta automáticamente pisos y muros con TensorFlow.js + DeepLab (ADE20K)
 * y aplica texturas de productos seleccionados.
 *
 * ADE20K class indices que usamos:
 *   0  = wall  (muro)
 *   3  = floor (piso)
 */
(function () {
  'use strict';

  const ADE20K_WALL  = 0;
  const ADE20K_FLOOR = 3;
  const MAX_DIM = 1280; // limita tamaño de imagen para perf

  class Visualizer {
    constructor(root) {
      this.root = root;
      this.fileInput     = root.querySelector('[data-vis-file]');
      this.canvas        = root.querySelector('[data-vis-canvas]');
      this.placeholder   = root.querySelector('[data-vis-placeholder]');
      this.loader        = root.querySelector('[data-vis-loader]');
      this.loaderText    = root.querySelector('[data-vis-loader-text]');
      this.tools         = root.querySelector('[data-vis-tools]');
      this.statusEl      = root.querySelector('[data-vis-status]');
      this.categorySelect = root.querySelector('[data-vis-category]');
      this.productsList   = root.querySelector('[data-vis-products]');
      this.surfaceBtns   = root.querySelectorAll('[data-vis-surface]');
      this.opacityInput  = root.querySelector('[data-vis-opacity]');
      this.resetBtn      = root.querySelector('[data-vis-reset]');
      this.newBtn        = root.querySelector('[data-vis-new]');

      this.ctx = this.canvas.getContext('2d');

      this.model = null;
      this.modelLoading = null;
      this.originalImage = null;     // Image element del upload
      this.imgWidth = 0;
      this.imgHeight = 0;
      this.segmentation = null;       // { legend, height, width, segmentationMap }
      this.floorMask = null;          // Uint8Array (1 donde es piso)
      this.wallMask  = null;          // Uint8Array (1 donde es muro)
      this.currentSurface = 'floor';
      this.currentProduct = null;     // { handle, title, imageUrl }
      this.opacity = 0.85;

      this.bindEvents();
      this.loadProductsForCollection('');
    }

    // ---------- Status & Loader ----------
    setStatus(msg, state) {
      this.statusEl.textContent = msg || '';
      if (state) this.statusEl.setAttribute('data-state', state);
      else this.statusEl.removeAttribute('data-state');
    }
    showLoader(text) {
      if (text) this.loaderText.textContent = text;
      this.loader.hidden = false;
    }
    hideLoader() { this.loader.hidden = true; }

    // ---------- Eventos ----------
    bindEvents() {
      this.fileInput.addEventListener('change', (e) => this.onFileSelected(e));
      this.categorySelect.addEventListener('change', () => {
        this.loadProductsForCollection(this.categorySelect.value);
      });
      this.surfaceBtns.forEach(btn => {
        btn.addEventListener('click', () => this.setSurface(btn.dataset.visSurface));
      });
      this.opacityInput.addEventListener('input', () => {
        this.opacity = parseInt(this.opacityInput.value, 10) / 100;
        if (this.currentProduct) this.applyTexture();
      });
      this.resetBtn.addEventListener('click', () => this.resetTexture());
      this.newBtn.addEventListener('click', () => {
        this.fileInput.value = '';
        this.fileInput.click();
      });
    }

    setSurface(s) {
      this.currentSurface = s;
      this.surfaceBtns.forEach(b => b.classList.toggle('is-active', b.dataset.visSurface === s));
      if (this.currentProduct) this.applyTexture();
    }

    // ---------- Productos ----------
    async loadProductsForCollection(handle) {
      this.productsList.innerHTML = '<div class="lcc-vis__products-empty">Cargando productos…</div>';
      try {
        const url = handle
          ? `/collections/${handle}/products.json?limit=50`
          : `/products.json?limit=50`;
        const res = await fetch(url);
        if (!res.ok) throw new Error('No se pudo cargar productos');
        const data = await res.json();
        const products = data.products || [];
        this.renderProducts(products);
      } catch (err) {
        this.productsList.innerHTML = '<div class="lcc-vis__products-empty">Error cargando productos</div>';
        console.error('[lcc-vis] loadProducts', err);
      }
    }

    renderProducts(products) {
      this.productsList.innerHTML = '';
      if (!products.length) {
        this.productsList.innerHTML = '<div class="lcc-vis__products-empty">Sin productos en esta categoría</div>';
        return;
      }
      products.forEach(p => {
        const img = (p.images && p.images[0]) ? p.images[0].src : null;
        if (!img) return;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'lcc-vis__product';
        btn.innerHTML = `
          <img src="${img}" alt="${p.title}" loading="lazy">
          <div class="lcc-vis__product-info">
            <div class="lcc-vis__product-title">${p.title}</div>
          </div>
        `;
        btn.addEventListener('click', () => this.selectProduct({ handle: p.handle, title: p.title, imageUrl: img }, btn));
        this.productsList.appendChild(btn);
      });
    }

    async selectProduct(product, btnEl) {
      // Highlight
      this.productsList.querySelectorAll('.lcc-vis__product.is-selected').forEach(el => el.classList.remove('is-selected'));
      if (btnEl) btnEl.classList.add('is-selected');

      this.currentProduct = product;
      if (!this.originalImage) {
        this.setStatus('Sube primero una foto de tu ambiente para aplicar este producto.', 'info');
        return;
      }
      if (!this.segmentation) {
        this.setStatus('Aún procesando tu imagen…', 'info');
        return;
      }
      await this.applyTexture();
    }

    // ---------- Upload ----------
    async onFileSelected(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      if (file.size > 8 * 1024 * 1024) {
        this.setStatus('La imagen es demasiado grande (máx. 8 MB).', 'error');
        return;
      }
      this.setStatus('');
      this.placeholder.hidden = true;
      this.canvas.hidden = false;
      this.tools.hidden = true;
      this.segmentation = null;
      this.floorMask = null;
      this.wallMask = null;

      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = async () => {
        URL.revokeObjectURL(url);
        // Redimensionar si es muy grande
        const scale = Math.min(1, MAX_DIM / Math.max(img.naturalWidth, img.naturalHeight));
        this.imgWidth  = Math.round(img.naturalWidth * scale);
        this.imgHeight = Math.round(img.naturalHeight * scale);
        this.canvas.width  = this.imgWidth;
        this.canvas.height = this.imgHeight;
        this.ctx.drawImage(img, 0, 0, this.imgWidth, this.imgHeight);
        this.originalImage = img;
        this.originalImageData = this.ctx.getImageData(0, 0, this.imgWidth, this.imgHeight);

        await this.ensureModelLoaded();
        await this.runSegmentation();
      };
      img.onerror = () => this.setStatus('No pudimos leer la imagen.', 'error');
      img.src = url;
    }

    // ---------- Modelo IA ----------
    async ensureModelLoaded() {
      if (this.model) return;
      if (this.modelLoading) return this.modelLoading;

      this.showLoader('Cargando modelo de IA (primera vez ~12 MB)…');
      this.setStatus('Descargando modelo…', 'info');

      this.modelLoading = (async () => {
        // Esperar a que tf y deeplab estén cargados
        for (let i = 0; i < 50 && (typeof tf === 'undefined' || typeof deeplab === 'undefined'); i++) {
          await new Promise(r => setTimeout(r, 100));
        }
        if (typeof tf === 'undefined' || typeof deeplab === 'undefined') {
          throw new Error('No se pudo cargar TensorFlow.js');
        }
        this.model = await deeplab.load({ base: 'ade20k', quantizationBytes: 2 });
        return this.model;
      })();
      await this.modelLoading;
      this.hideLoader();
    }

    async runSegmentation() {
      if (!this.originalImage) return;
      this.showLoader('Detectando piso y muros…');
      this.setStatus('Analizando imagen…', 'info');

      try {
        // Pasar el canvas al modelo
        const prediction = await this.model.segment(this.canvas);
        this.segmentation = prediction;

        // Construir máscaras binarias del tamaño de la imagen
        // prediction.segmentationMap es Uint8ClampedArray RGBA del tamaño width x height del resultado
        // prediction.width, prediction.height son los tamaños del output
        const { segmentationMap, legend, width: pw, height: ph } = prediction;

        // El segmentationMap está RGBA codificado por clase. Necesitamos un mapa de clases.
        // Usamos rawMap si está disponible — sino, derivamos por color desde legend.
        // En @tensorflow-models/deeplab, el segmentationMap es Uint8ClampedArray con RGBA por píxel
        // donde RGB representa el color de la clase. Reconstruimos índices comparando con legend.

        const classByColor = {};
        if (legend) {
          Object.keys(legend).forEach((className, idx) => {
            const rgb = legend[className]; // [r,g,b]
            const key = rgb[0] + ',' + rgb[1] + ',' + rgb[2];
            classByColor[key] = className;
          });
        }

        // Construimos máscaras a tamaño pw x ph
        const npix = pw * ph;
        const floorMaskSmall = new Uint8Array(npix);
        const wallMaskSmall  = new Uint8Array(npix);

        // Colores conocidos (ADE20K legend de deeplab.js):
        //   wall  = [120, 120, 120]
        //   floor = [80, 50, 50] (color asignado por la librería; verificamos via legend)
        let wallColor  = legend && legend['wall']  ? legend['wall']  : [120, 120, 120];
        let floorColor = legend && legend['floor'] ? legend['floor'] : [80, 50, 50];

        for (let i = 0; i < npix; i++) {
          const r = segmentationMap[i * 4];
          const g = segmentationMap[i * 4 + 1];
          const b = segmentationMap[i * 4 + 2];
          if (r === wallColor[0]  && g === wallColor[1]  && b === wallColor[2])  wallMaskSmall[i]  = 1;
          if (r === floorColor[0] && g === floorColor[1] && b === floorColor[2]) floorMaskSmall[i] = 1;
        }

        // Re-escalar máscaras al tamaño del canvas (this.imgWidth x this.imgHeight)
        this.floorMask = this.resizeMask(floorMaskSmall, pw, ph, this.imgWidth, this.imgHeight);
        this.wallMask  = this.resizeMask(wallMaskSmall,  pw, ph, this.imgWidth, this.imgHeight);

        const floorPx = countOnes(this.floorMask);
        const wallPx  = countOnes(this.wallMask);

        this.hideLoader();
        this.tools.hidden = false;

        if (floorPx === 0 && wallPx === 0) {
          this.setStatus('No detectamos piso ni muro en la imagen. Prueba con otra foto donde el piso o muro sea más visible.', 'error');
          return;
        }

        // Auto-elegir superficie principal
        if (floorPx >= wallPx) this.setSurface('floor');
        else this.setSurface('wall');

        const partsDetected = [];
        if (floorPx > 0) partsDetected.push('piso');
        if (wallPx > 0)  partsDetected.push('muro');
        this.setStatus(`✓ Detectado: ${partsDetected.join(' y ')}. Elige un producto del carrusel para aplicarlo.`, 'ok');

        // Si ya hay producto seleccionado, aplicarlo
        if (this.currentProduct) await this.applyTexture();
      } catch (err) {
        console.error('[lcc-vis] segmentation error', err);
        this.hideLoader();
        this.setStatus('Error procesando la imagen. Intenta con otra foto.', 'error');
      }
    }

    // Resize bilineal de una máscara binaria
    resizeMask(src, sw, sh, dw, dh) {
      const dst = new Uint8Array(dw * dh);
      const xRatio = sw / dw;
      const yRatio = sh / dh;
      for (let y = 0; y < dh; y++) {
        const sy = Math.min(sh - 1, Math.floor(y * yRatio));
        for (let x = 0; x < dw; x++) {
          const sx = Math.min(sw - 1, Math.floor(x * xRatio));
          dst[y * dw + x] = src[sy * sw + sx];
        }
      }
      return dst;
    }

    // ---------- Aplicar textura ----------
    async applyTexture() {
      if (!this.currentProduct || !this.originalImageData) return;
      const mask = this.currentSurface === 'floor' ? this.floorMask : this.wallMask;
      if (!mask) return;

      const maskCount = countOnes(mask);
      if (maskCount === 0) {
        this.setStatus(`No se detectó ${this.currentSurface === 'floor' ? 'piso' : 'muro'} en esta imagen.`, 'error');
        return;
      }

      this.setStatus('Aplicando textura…', 'info');
      try {
        const texture = await loadImage(this.currentProduct.imageUrl);
        // Restaurar imagen original
        this.ctx.putImageData(this.originalImageData, 0, 0);

        // Crear canvas de patrón con la textura tileable
        const tileSize = this.currentSurface === 'floor' ? 220 : 260;
        const patternCanvas = document.createElement('canvas');
        patternCanvas.width = tileSize;
        patternCanvas.height = tileSize;
        const pctx = patternCanvas.getContext('2d');
        pctx.drawImage(texture, 0, 0, tileSize, tileSize);

        // Canvas auxiliar con el patrón aplicado en toda el área
        const overlay = document.createElement('canvas');
        overlay.width = this.imgWidth;
        overlay.height = this.imgHeight;
        const octx = overlay.getContext('2d');
        const pattern = octx.createPattern(patternCanvas, 'repeat');
        octx.fillStyle = pattern;
        octx.fillRect(0, 0, this.imgWidth, this.imgHeight);
        const overlayData = octx.getImageData(0, 0, this.imgWidth, this.imgHeight);

        // Aplicar la textura solo donde la máscara es 1, preservando luminosidad
        // mediante multiply: out = orig * tex / 255 (aproximación de iluminación)
        const orig = this.originalImageData.data;
        const tex  = overlayData.data;
        const out  = this.ctx.createImageData(this.imgWidth, this.imgHeight);
        const o = out.data;
        const alpha = this.opacity; // 0..1

        for (let i = 0; i < mask.length; i++) {
          const p = i * 4;
          if (mask[i]) {
            // Luminosidad del original (gris)
            const lum = (orig[p] * 0.299 + orig[p+1] * 0.587 + orig[p+2] * 0.114) / 255;
            // Multiply blend: textura * luminosidad, mezclado con orig según alpha
            const tr = tex[p]   * lum;
            const tg = tex[p+1] * lum;
            const tb = tex[p+2] * lum;
            o[p]   = orig[p]   * (1 - alpha) + tr * alpha;
            o[p+1] = orig[p+1] * (1 - alpha) + tg * alpha;
            o[p+2] = orig[p+2] * (1 - alpha) + tb * alpha;
            o[p+3] = 255;
          } else {
            o[p]   = orig[p];
            o[p+1] = orig[p+1];
            o[p+2] = orig[p+2];
            o[p+3] = 255;
          }
        }
        this.ctx.putImageData(out, 0, 0);

        this.setStatus(`✓ Visualizando "${this.currentProduct.title}" en ${this.currentSurface === 'floor' ? 'el piso' : 'el muro'}.`, 'ok');
      } catch (err) {
        console.error('[lcc-vis] applyTexture error', err);
        this.setStatus('Error aplicando la textura.', 'error');
      }
    }

    resetTexture() {
      if (!this.originalImageData) return;
      this.ctx.putImageData(this.originalImageData, 0, 0);
      this.productsList.querySelectorAll('.is-selected').forEach(el => el.classList.remove('is-selected'));
      this.currentProduct = null;
      this.setStatus('Textura removida.', 'info');
    }
  }

  function loadImage(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
  }

  function countOnes(arr) {
    let n = 0;
    for (let i = 0; i < arr.length; i++) if (arr[i]) n++;
    return n;
  }

  function init() {
    const root = document.querySelector('[data-vis-app]');
    if (!root) return;
    new Visualizer(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
